/**
 * 計測結果をグラフの系列にする（complexity-meter.md §3.2, §4, §6）。DOM に触れない純関数。
 * - ステップ数（主）: 送ったすべての点。打ち切りの点は下限（≥）として ✕ 印と破線で描く。
 * - 実行時間（補助）: N ≥ 10^4 の点だけ。timedOut は 3,000ms の天井に ✕ 印。
 *   実行時エラー（RE）の点は時間が意味を持たないので描かない（§6.1）。
 */
import {
  chartPoints,
  formatDurationMs,
  formatTicks,
  groupDigits,
  TIMEOUT_CEILING_MS,
  type MeterPoint,
  type SkipReason,
} from '../../lib/complexity';

export type SeriesKey = 1 | 2;

/** 計測点の状態（§6.1）。tle = 打ち切り、re = 0 以外の終了コード（打ち切りでない）。 */
export type PointStatus = 'ok' | 'tle' | 're';

export function pointStatus(p: Pick<MeterPoint, 'timedOut' | 'exitCode'>): PointStatus {
  if (p.timedOut) return 'tle';
  if (p.exitCode !== 0) return 're';
  return 'ok';
}

/** RE の表示（§6.1）。 */
export function reText(exitCode: number): string {
  return `RE（終了コード ${exitCode}）`;
}
export type ChartKind = 'steps' | 'duration';

export interface ChartPoint {
  n: number;
  value: number;
  /** 打ち切り（TLE）・下限・実行時エラーの点（✕ 印）。 */
  cut: boolean;
  status: PointStatus;
  /** ツールチップと表の値の文字。 */
  text: string;
}

export interface ChartSeries {
  key: SeriesKey;
  label: string;
  points: ChartPoint[];
}

export interface SeriesInput {
  key: SeriesKey;
  label: string;
  points: readonly MeterPoint[];
}

/** 実測の 1 系列（表とグラフの元）。 */
export interface ResultSeries extends SeriesInput {
  skipped: readonly { n: number; reason: SkipReason }[];
  ops?: string | undefined;
}

export function toChartSeries(kind: ChartKind, input: SeriesInput): ChartSeries {
  if (kind === 'steps') {
    const points = input.points
      .filter((p): p is MeterPoint & { ticks: number } => p.ticks !== null)
      .map((p) => {
        const status = pointStatus(p);
        const ticks = `${formatTicks(p.ticks, p.ticksLowerBound)} 回`;
        return {
          n: p.n,
          value: p.ticks,
          cut: p.ticksLowerBound || status !== 'ok',
          status,
          text: status === 're' ? `${ticks}（${reText(p.exitCode)}）` : ticks,
        };
      });
    return { key: input.key, label: input.label, points };
  }
  const points = chartPoints(input.points.filter((p) => pointStatus(p) !== 're')).map(
    (p): ChartPoint => ({
      n: p.n,
      value: p.ms,
      cut: p.timedOut,
      status: p.timedOut ? 'tle' : 'ok',
      text: formatDurationMs(p.ms, p.timedOut),
    }),
  );
  return { key: input.key, label: input.label, points };
}

/** y 軸の上端。実行時間は 0〜3,000ms × 1.1 で固定（layout-and-navigation.md §6）。 */
export function yDomainMax(kind: ChartKind, series: readonly ChartSeries[]): number {
  if (kind === 'duration') return Math.round(TIMEOUT_CEILING_MS * 1.1);
  let max = 0;
  for (const s of series) for (const p of s.points) max = Math.max(max, p.value);
  return max > 0 ? max : 1;
}

/** すべての系列の N（昇順、重複なし）。十字線はこの値にスナップする。 */
export function unionNs(series: readonly ChartSeries[]): number[] {
  const set = new Set<number>();
  for (const s of series) for (const p of s.points) set.add(p.n);
  return [...set].sort((a, b) => a - b);
}

/** 実線で結ぶ点と、打ち切りへ破線で結ぶ点に分ける。 */
export function splitSegments(points: readonly ChartPoint[]): {
  solid: ChartPoint[];
  dashed: ChartPoint[];
} {
  const solid = points.filter((p) => !p.cut);
  const cut = points.filter((p) => p.cut);
  const last = solid[solid.length - 1];
  const dashed = cut.length > 0 && last ? [last, ...cut] : [];
  return { solid, dashed };
}

/**
 * 直接ラベルの y を、互いに minGap 以上離す（上から順に押し下げる）。
 * 戻り値は入力と同じ順。
 */
export function dodgeLabels(ys: readonly number[], minGap = 14): number[] {
  const order = ys.map((y, i) => ({ y, i })).sort((a, b) => a.y - b.y);
  const out = [...ys];
  let prev = -Infinity;
  for (const o of order) {
    const y = Math.max(o.y, prev + minGap);
    out[o.i] = y;
    prev = y;
  }
  return out;
}

/** グラフの要約（aria-describedby）。値はツールチップだけに閉じ込めない。 */
export function chartSummary(kind: ChartKind, series: readonly ChartSeries[]): string {
  if (series.every((s) => s.points.length === 0)) {
    return kind === 'duration'
      ? 'N = 10,000 以上の点がまだありません。'
      : 'ステップ数の記録がまだありません。';
  }
  const parts = series.map((s) => {
    const done = s.points.filter((p) => !p.cut);
    const cut = s.points.filter((p) => p.cut && p.status !== 're');
    const re = s.points.filter((p) => p.status === 're');
    const last = done[done.length - 1];
    const head = last
      ? `${s.label}は N = ${groupDigits(last.n)} で ${last.text}`
      : `${s.label}は完了した点がありません`;
    const tail =
      cut.length > 0 ? `、N = ${cut.map((p) => groupDigits(p.n)).join('、')} は打ち切り` : '';
    const reTail =
      re.length > 0 ? `、N = ${re.map((p) => groupDigits(p.n)).join('、')} は実行時エラー` : '';
    return `${head}${tail}${reTail}。`;
  });
  return `${parts.join('')}矢印キーで点を移動できます。`;
}
